/**
 * "What we add" options for the Text Notes Routing table, derived from the
 * configured body columns instead of a fixed list.
 *
 * pharmacy_rpa gates a note block by a *label*, not by field name
 * (`text_note_content_helpers/download.py`): a column's stored
 * `what_we_add_label` wins, else a built-in field-name map, and a column in
 * neither renders unconditionally. So the option value is that gating label,
 * and selecting a column that has no label yet stamps one onto it on save.
 */
import { BodyColumnRow } from "./textNotesContentUtils";
import { RoutingRuleRow } from "./textNotesRoutingUtils";

/**
 * The screenshot block's selection label. It is not a body column, so it stays a
 * fixed option; mirrors `ScreenshotConfig.what_we_add_label` in pharmacy_rpa.
 */
export const PORTAL_SCREENSHOT_LABEL = "Portal Screenshot";

/**
 * Fallback field-name → selection-label map, mirroring `_FIELD_TO_LABEL` in
 * pharmacy_rpa. A column with no stored `what_we_add_label` is gated by its
 * entry here, so these strings must match verbatim or saved rules stop
 * selecting the block.
 */
export const BUILTIN_WHAT_WE_ADD_LABELS: Record<string, string> = {
  patient_info: "Patient Details",
  pa_case_id: "Key",
  cmm_key: "Key",
  drug_info: "Drug (submitted, not input)",
  form_info: "Form",
};

/**
 * The value a routing rule must list for this column's block to render.
 *
 * @param column - Body column row
 * @returns Stored label, else the built-in map entry, else the column's own label
 */
export function resolveWhatWeAddLabel(column: BodyColumnRow): string {
  return (
    column.whatWeAddLabel.trim() ||
    BUILTIN_WHAT_WE_ADD_LABELS[column.fieldName] ||
    column.displayLabel.trim() ||
    column.fieldName
  );
}

/**
 * Builds the "What we add" options from the current body columns plus the fixed
 * Portal Screenshot entry. Columns resolving to the same label (`pa_case_id` and
 * `cmm_key` both map to "Key") collapse into one option.
 *
 * @param bodyColumns - Current Text Note body column rows
 * @returns Deduped options in body-column order, screenshot last
 */
export function buildWhatWeAddOptions(
  bodyColumns: BodyColumnRow[],
): { value: string; label: string }[] {
  const byValue = new Map<string, string>();
  bodyColumns.forEach((column) => {
    const value = resolveWhatWeAddLabel(column);
    if (value === "" || byValue.has(value)) return;
    byValue.set(value, column.displayLabel.trim() || column.fieldName || value);
  });
  byValue.set(PORTAL_SCREENSHOT_LABEL, PORTAL_SCREENSHOT_LABEL);
  return Array.from(byValue, ([value, label]) => ({ value, label }));
}

/**
 * Drops a deleted body column's label from every routing rule that selected it.
 *
 * The label survives when another remaining column still resolves to it (e.g.
 * `pa_case_id` and `cmm_key` both map to "Key") or when it is the screenshot
 * label, which is not backed by a body column.
 *
 * @param routingRows - Current routing rules
 * @param removedColumn - The body column being deleted
 * @param remainingColumns - Body columns left after the deletion
 * @returns Routing rules with the orphaned label removed
 */
export function pruneDeletedWhatWeAddLabel(
  routingRows: RoutingRuleRow[],
  removedColumn: BodyColumnRow,
  remainingColumns: BodyColumnRow[],
): RoutingRuleRow[] {
  const label = resolveWhatWeAddLabel(removedColumn);
  if (label === "" || label === PORTAL_SCREENSHOT_LABEL) return routingRows;
  if (remainingColumns.some((col) => resolveWhatWeAddLabel(col) === label)) {
    return routingRows;
  }
  return routingRows.map((row) =>
    row.whatWeAdd.includes(label)
      ? { ...row, whatWeAdd: row.whatWeAdd.filter((v) => v !== label) }
      : row,
  );
}

/**
 * Persists `what_we_add_label` on each body column a routing rule now selects.
 *
 * Without this a column outside the built-in map is ungated, so the RPA would
 * ignore the selection. Columns that already carry a label, are covered by the
 * built-in map, or are named by no rule are returned untouched — their current
 * render behaviour is preserved.
 *
 * @param bodyColumns - Current body column rows
 * @param routingRows - Current routing rules, supplying the selected labels
 * @returns Rows with labels stamped where a selection needs one
 */
export function stampSelectedWhatWeAddLabels(
  bodyColumns: BodyColumnRow[],
  routingRows: RoutingRuleRow[],
): BodyColumnRow[] {
  const selected = new Set(routingRows.flatMap((row) => row.whatWeAdd));
  return bodyColumns.map((column) => {
    if (column.whatWeAddLabel.trim() !== "") return column;
    if (BUILTIN_WHAT_WE_ADD_LABELS[column.fieldName]) return column;
    const label = resolveWhatWeAddLabel(column);
    return selected.has(label) ? { ...column, whatWeAddLabel: label } : column;
  });
}
